import React, { useEffect, useMemo } from 'react';
import classNames from 'classnames';
import MetricsCard from '@/pages/dashboard/components/MetricsCard';
import { DATA_TYPE_OBJECT, getName } from '@home/utils/common';
import MetricsCardTrendChart from '@/pages/dashboard/components/MetricsCardTrendChart';
import { jumpDetail, openWithParams } from '@/pages/dashboard/utils/utils';
import useStudioExplain from '@home/hooks/useStudioExplain';
import LoadingFailed from '@/pages/dashboard/components/LoadingFailed';
import { mapObjectValue } from '@home/pages/studio/utils';
import { extractParamsFromCarryFilter, getFilterConfig } from '../utils';
import { useIntl } from 'umi';
import { LANGUAGE_EN } from '@home/_config/config';
import moment from 'moment';
import './index.scss';
import { dynamicString } from '@home/utils/dynamicString';
import { JumpType, navigate } from '@home/utils/utils';

const StudioMetricCardTrend = (props: any) => {
  const { config, apiDataKey, filter, className, errorStyle, filterConfig, data: propsData, changeData } = props;
  const {
    titleEn,
    titleCn,
    subtitleEn,
    titleEnChange,
    titleCnChange,
    isRightCard,
    isLeftCard,
    imgUrl,
    dodData,
    formatRule,
    titleFormatRule,
    metricKey,
    preUnit: unit,
    endUnit: unitEnd,
    isIconTips = false,
    longTrend = false,
    rainbowKey,
    explainEn,
    explainCn,
    trendConfig,
    link,
    jumpParams = {},
    jumpType = JumpType.Window,
    cardType,
    extraCarryParams = {},
    yAxisNameEn,
    yAxisNameCn,
    showNoData,
    nodataMsgEn= "No data under current filter selections.<br />Please modify the date range for data.",
    nodataMsgCn= "当前筛选条件下无数据。<br />请调整日期范围以查看数据。",
    subtitleKey,
    ...restConfig
  } = config || {};
  const { data, errorMsg } = props?.data?.[apiDataKey] || {};
  const { trend_list: trendList, card_list: cardList } = data || {};
  const { date_type: dataType, isSingle, start_time: startTime, end_time: endTime, payType, selectorList } = filter || {};
  const carryFilter = getFilterConfig(selectorList, filterConfig);
  const { locale } = useIntl();
  const isEn = locale === LANGUAGE_EN;
  const yAxisName = getName(isEn, yAxisNameEn, yAxisNameCn);
  // 判断趋势图的指标数据是否为空
  const isEmptyData =
    trendList?.every((item: { metrics: { [x: string]: string | number; }; }) => {
      const metric = item.metrics?.[metricKey];
      return !metric || Object.values(metric).every(val => val == null || val === "");
    });

  // 获取七彩石指标注释
  const { tipEn, tipCn } = useStudioExplain(rainbowKey, explainEn, explainCn);
  const nodataMsg = getName(isEn, nodataMsgEn, nodataMsgCn);
  // 副标题支持数据配置
  useEffect(() => {
    if (cardList) {
      const metricItem = cardList?.find(
        (item: any) => item.metrics_name === metricKey
      );
  
      if (metricItem) {
        changeData(subtitleKey, metricItem?.time);
      }
    }
  }, [metricKey, subtitleKey, cardList]);

  // 根据配置组装对应DodData
  const selectedDataField = DATA_TYPE_OBJECT[dataType] || 'Day';
  const cardData = cardList?.find((card: { metrics_name: string; }) => card?.metrics_name === metricKey);
  const dodValues = Object.entries(dodData?.[selectedDataField] ?? {})?.map(([key, dodItem]: [string, any]) => {
    if (!isSingle && key !== 'change' && key !== 'reviews') {
      return null;
    }
    if (isSingle && key === 'change') {
      return null;
    }
    if (key === 'dod' || key === 'sdlw' || key === 'change' || key === 'reviews') {
      const dataToUse = cardData?.[key];
      return {
        titleEn: dodItem?.titleEn,
        titleCn: dodItem?.titleCn,
        formatRule: dodItem?.formatRule,
        extendFormatRule: dodItem?.extendFormatRule,
        thisTime: dataToUse?.this_time || '',
        lastTime: dataToUse?.last_time || '',
        value: dataToUse?.show_value || '',
        thisValue: dataToUse?.this_value || '',
        lastValue: dataToUse?.last_value || '',
        hideBubbleContent: dodItem?.hideBubbleContent,
        hideDodIcon: dodItem?.hideDodIcon ?? dodItem?.hideBubbleContent,
        style: dodItem?.style,
        className: dodItem?.className,
      };
    }
    return null; // 如果 key 不符合条件，返回 null
  }).filter(Boolean);

  const preUnit = cardData?.value === '-' ? '' : unit;
  const endUnit = cardData?.value === '-' ? '' : unitEnd;

  // 根据 isSingle 动态修改 titleEn 和 titleCn
  const titleEN = isSingle ? titleEn : (titleEnChange || titleEn);
  const titleCN = isSingle ? titleCn : (titleCnChange || titleCn);

  // 筛选器继承参数
  const filterCarry = useMemo(() => {
    let baseParams;

    if (cardType === 'opinion') {
      baseParams = {
        start_time: startTime,
        end_time: endTime,
        date_type: dataType,
      };
    } else if (cardType === 'intelligence') {

      baseParams = {
        start_date: moment(startTime).format('YYYY-MM-DD'),
        end_date: moment(endTime).format('YYYY-MM-DD'),
        granularity: selectedDataField?.toLowerCase(),
      };
    } else {
      baseParams = {
        start: startTime,
        end: endTime,
        timeType: DATA_TYPE_OBJECT[dataType],
        ...(payType ? { payment_type: payType } : {}),
      };
    }

    // 合并额外参数
    return { ...baseParams, ...extractParamsFromCarryFilter(extraCarryParams, carryFilter) };
  }, [filter, cardType, extraCarryParams, carryFilter]);

  // 卡片下钻
  const onClick = () => {
    if (link) {
      // 筛选器继承跳转
      if (jumpParams?.filterValue && isSingle && jumpParams?.isComplete && filter?.jumpTime) {
        jumpParams.filterValue.rangeTime = filter?.jumpTime;
      }

      navigate(link, { jumpType, windowParams: filterCarry });

      // 下钻逻辑
      // if (jumpParams?.filterValue) {
      //   jumpParams.filterValue = {
      //     ...mapObjectValue(jumpParams.filterValue, filter,),
      //     ...extractParamsFromCarryFilter(extraCarryParams, carryFilter)
      //   };
      // }
      // jumpDetail(link, jumpParams);

    }
  };

  if (errorMsg) {
    return <div style={{
      height: errorStyle?.height,
      color: '#444',
      background: '#fff',
      borderRadius: 8,
    }}><LoadingFailed /></div>;
  }

  return (
    <MetricsCard
      titleEn={titleEN}
      titleCn={titleCN}
      subtitleEn={dynamicString(subtitleEn, propsData) || ''}
      tipsEn={tipEn}
      tipsCn={tipCn}
      isIconTips={isIconTips}
      className={classNames(
        'dbh-studio-home-rank-card',
        { 'no-link': !link },
        className// 动态类名
      )}
      nodataMsg={nodataMsg}
      showNoData={showNoData && isEmptyData}
      imgUrl={imgUrl}
      formatRule={titleFormatRule ? titleFormatRule : formatRule}
      value={cardData?.value}
      preUnit={preUnit}
      endUnit={endUnit}
      dodData={dodValues}
      link={link ? onClick : null}
      metricsChart={<MetricsCardTrendChart
        titleEn={titleEN}
        titleCn={titleCN}
        className={longTrend ? "dbh-studio-home-rank-card-trend" : ''}
        isRightCard={isRightCard}
        isLeftCard={isLeftCard}
        metricData={trendList}
        loading={false}
        error={false}
        filterVal={{ timeType: selectedDataField }}
        componentMetrics={[trendConfig?.[selectedDataField] || {}]}
        yAxisName={yAxisName}
        isEmptyData={isEmptyData}
      />}
      {...restConfig}
    >
    </MetricsCard>
  );
};

export default StudioMetricCardTrend;